import { Copy, KeyRound, Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createApiKeySchema,
  type CreateApiKeyInput,
} from '@obraplan/core/application/contracts/api-keys';
import type { ApiKeyView } from '@obraplan/core/application/use-cases/access/api-keys';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { useContainer, useSession } from '@/features/session/use-session';
import { formatDateTime } from '@/lib/format';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

/**
 * As chaves de API, na tela de perfis (07/10/2026).
 *
 * A credencial de um agente de IA: lê o sistema inteiro e não escreve nada. O cartão
 * emite, lista e revoga; o segredo aparece **uma vez**, no diálogo da criação, e depois
 * só o prefixo — não há "mostrar de novo", e é assim de propósito.
 */

export function ApiKeysCard() {
  const container = useContainer();
  const session = useSession();

  const [createOpen, setCreateOpen] = useState(false);
  const [secret, setSecret] = useState<{ name: string; secret: string } | undefined>(undefined);
  const [toRevoke, setToRevoke] = useState<ApiKeyView | undefined>(undefined);
  const [copied, setCopied] = useState(false);

  const keys = useLive(() => container.listApiKeys.execute(session), [session.userId]);

  const form = useForm<CreateApiKeyInput>({
    resolver: zodResolver(createApiKeySchema),
    defaultValues: { name: '' },
  });

  const [createKey, createState] = useCommand(
    (values: CreateApiKeyInput) => container.createApiKey.execute(values, session),
    {
      successMessage: 'Chave criada. Copie o segredo agora: ele não aparece de novo.',
      silent: true,
      onSuccess: (created) => {
        form.reset();
        setCreateOpen(false);
        setCopied(false);
        setSecret({ name: created.key.name, secret: created.secret });
      },
    },
  );

  const [revokeKey, revokeState] = useCommand(
    (apiKeyId: ApiKeyView['id']) => container.revokeApiKey.execute({ apiKeyId }, session),
    { successMessage: 'Chave revogada.', onSuccess: () => setToRevoke(undefined) },
  );

  const copiar = async (value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <CardTitle>Chaves de API</CardTitle>
          <CardDescription>
            A credencial de um agente de IA: lê o sistema inteiro e não escreve nada. Vale até ser
            revogada.
          </CardDescription>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setCreateOpen(true)}>
          <Plus aria-hidden="true" />
          Nova chave
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {keys === undefined ? (
          <LoadingState />
        ) : !keys.ok ? (
          <ErrorState message={keys.error.message} />
        ) : keys.value.length === 0 ? (
          <EmptyState
            icon={<KeyRound className="size-8" />}
            title="Nenhuma chave emitida"
            description="Crie uma chave para conectar um agente de IA ao sistema, em modo de leitura."
          />
        ) : (
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Prefixo</TableHead>
                  <TableHead>Criada em</TableHead>
                  <TableHead>Último uso</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.value.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell>
                      <code className="text-xs">{key.tokenPrefix}…</code>
                    </TableCell>
                    <TableCell>{formatDateTime(key.createdAt)}</TableCell>
                    <TableCell>
                      {key.lastUsedAt === undefined ? '—' : formatDateTime(key.lastUsedAt)}
                    </TableCell>
                    <TableCell>
                      {key.revokedAt === undefined ? (
                        <Badge tone="ok">Ativa</Badge>
                      ) : (
                        <Badge tone="neutral">Revogada em {formatDateTime(key.revokedAt)}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {key.revokedAt === undefined && (
                        <Button variant="ghost" size="sm" onClick={() => setToRevoke(key)}>
                          Revogar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableWrapper>
        )}
      </CardContent>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCreateOpen(false);
            form.reset();
          }
        }}
      >
        <DialogContent>
          <form
            onSubmit={(event) => {
              void form.handleSubmit((values) => createKey(values))(event);
            }}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>Nova chave de API</DialogTitle>
              <DialogDescription>
                Dê um nome que diga para que ela serve. O segredo aparece uma vez, logo depois.
              </DialogDescription>
            </DialogHeader>
            <Field
              label="Nome"
              htmlFor="api-key-name"
              required
              error={form.formState.errors.name?.message}
            >
              <Input
                id="api-key-name"
                autoFocus
                placeholder="Agente de planejamento"
                aria-invalid={form.formState.errors.name !== undefined}
                {...form.register('name')}
              />
            </Field>
            {createState.error !== undefined && (
              <p role="alert" className="text-sm text-(--color-state-danger)">
                {createState.error.message}
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={createState.isPending}>
                {createState.isPending ? 'Criando…' : 'Criar chave'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={secret !== undefined}
        onOpenChange={(open) => {
          if (!open) setSecret(undefined);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copie o segredo agora</DialogTitle>
            <DialogDescription>
              Esta é a única vez que o segredo da chave “{secret?.name}” aparece. Guarde-o na
              configuração do agente; se perder, crie outra e revogue esta.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code
              className="flex-1 overflow-x-auto rounded-md border border-(--color-border-subtle) bg-(--color-surface-muted) px-3 py-2 text-xs"
              aria-label="Segredo da chave"
            >
              {secret?.secret}
            </code>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (secret !== undefined) void copiar(secret.secret);
              }}
            >
              <Copy aria-hidden="true" />
              {copied ? 'Copiado' : 'Copiar'}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="primary" onClick={() => setSecret(undefined)}>
              Já guardei
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={toRevoke !== undefined}
        onOpenChange={(open) => {
          if (!open) setToRevoke(undefined);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revogar a chave “{toRevoke?.name}”?</DialogTitle>
            <DialogDescription>
              O agente que a usa para de ler na hora. A revogação fica registrada na auditoria e não
              se desfaz — para voltar, crie outra chave.
            </DialogDescription>
          </DialogHeader>
          {revokeState.error !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {revokeState.error.message}
            </p>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setToRevoke(undefined)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              disabled={revokeState.isPending}
              onClick={() => {
                if (toRevoke !== undefined) void revokeKey(toRevoke.id);
              }}
            >
              {revokeState.isPending ? 'Revogando…' : 'Revogar chave'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
